import { COMPONENT_TYPES, ModalModes, PROGRAM_ATTRIBUTE_TYPES } from '../../Constant'
import {
    ColumnLayout,
    FormField,
    Input,
    Select,
    Textarea,
    Toggle,
    InputProps,
    SpaceBetween,
    Box,
    TokenGroup,
    Button,
} from '@amzn/awsui-components-react'
import ComponentOnChangeTemplate from './ComponentOnChangeTemplate'
import React from 'react'
import { getInputProperties, getOpEstimateValueSum } from './Utils'
import EmptyModalSection from './EmptyModalSection'
import useStore from '../../Store'

const GroupFormFieldGenerator = (props) => {
    const {
        attributes,
        state,
        inputChange,
        items,
        selectedItems,
        isInputInvalid,
        onIsInputInvalidChanged,
        onIsDataUnmodifiedChanged,
        isAddingProgram,
        modalMode,
        formErrors,
        setFormErrors,
        colNum,
    } = props
    const canAdmin = useStore((state) => state.canAdmin)
    const canSTL = useStore((state) => state.canSTL)
    const getAttrDisabled = (attr) => {
        return (
            attr.disabled ||
            (isAddingProgram && attr.attributeType !== PROGRAM_ATTRIBUTE_TYPES.LOCAL) ||
            modalMode === ModalModes.VIEW ||
            (!canAdmin && !canSTL)
        )
    }
    return (
        <ColumnLayout columns={colNum || 4}>
            {attributes.map((attr) => {
                let required = attr.required || attr.adminRequired
                if (required === undefined) {
                    required = attr.required
                }
                if (attr.showConditionally && !state[attr.conditionalOnAttr] === attr.showOnValue) {
                    return <></>
                }
                switch (attr.componentType) {
                    case COMPONENT_TYPES.INPUT_STRING:
                    case COMPONENT_TYPES.INPUT_INT:
                    case COMPONENT_TYPES.INPUT_FLOAT:
                        return (
                            <FormField
                                key={attr.id}
                                label={
                                    attr.disabled ? (
                                        <span> {attr.headerName} </span>
                                    ) : (
                                        <span>
                                            {attr.headerName}
                                            {!required && <i> - optional</i>}
                                        </span>
                                    )
                                }
                                description={attr.description}
                                errorText={required ? state[`${attr.id}_error`] : ''}
                                info={attr?.infoContent}
                            >
                                <SpaceBetween size={'xxs'} direction={'horizontal'}>
                                    <Input
                                        value={
                                            attr.disabled
                                                ? getOpEstimateValueSum(
                                                      state['op1_estimate_ct'],
                                                      state['op1_estimate_ff'],
                                                  )
                                                : state[attr.id]
                                        }
                                        onChange={({ detail }) =>
                                            ComponentOnChangeTemplate(
                                                attr.checkDuplicate,
                                                required,
                                                attr.componentType,
                                                attr.id,
                                                detail.value,
                                                attr.headerName,
                                                inputChange,
                                                selectedItems,
                                                items,
                                                isInputInvalid,
                                                onIsInputInvalidChanged,
                                                onIsDataUnmodifiedChanged,
                                                formErrors,
                                                setFormErrors,
                                            )
                                        }
                                        disabled={getAttrDisabled(attr)}
                                        type={
                                            getInputProperties(attr.componentType)
                                                .type as InputProps.Type
                                        }
                                        inputMode={
                                            getInputProperties(attr.componentType)
                                                .inputMode as InputProps.InputMode
                                        }
                                        step={
                                            getInputProperties(attr.componentType, state[attr.id])
                                                .step
                                        }
                                    ></Input>
                                </SpaceBetween>
                            </FormField>
                        )
                    case COMPONENT_TYPES.TEXTAREA:
                        return (
                            <FormField
                                key={attr.id}
                                label={
                                    <span>
                                        {attr.headerName}
                                        {!required && <i> - optional</i>}
                                    </span>
                                }
                                description={attr.description}
                                errorText={required ? state[`${attr.id}_error`] : ''}
                                info={attr?.infoContent}
                            >
                                <Textarea
                                    value={state[attr.id]}
                                    rows={5}
                                    onChange={({ detail }) =>
                                        ComponentOnChangeTemplate(
                                            attr.checkDuplicate,
                                            required,
                                            COMPONENT_TYPES.TEXTAREA,
                                            attr.id,
                                            detail.value,
                                            attr.headerName,
                                            inputChange,
                                            selectedItems,
                                            items,
                                            isInputInvalid,
                                            onIsInputInvalidChanged,
                                            onIsDataUnmodifiedChanged,
                                            formErrors,
                                            setFormErrors,
                                        )
                                    }
                                    disabled={getAttrDisabled(attr)}
                                    spellcheck
                                ></Textarea>
                            </FormField>
                        )
                    case COMPONENT_TYPES.TOGGLE:
                        return (
                            <FormField
                                key={attr.id}
                                label={
                                    <span>
                                        {attr.headerName}
                                        {!required && <i> - optional</i>}
                                    </span>
                                }
                                description={attr.description}
                                info={attr?.infoContent}
                            >
                                <Toggle
                                    onChange={({ detail }) => {
                                        inputChange(attr.id, detail.checked)
                                        onIsDataUnmodifiedChanged(false)
                                    }}
                                    checked={state[attr.id]}
                                    disabled={getAttrDisabled(attr)}
                                ></Toggle>
                            </FormField>
                        )
                    case COMPONENT_TYPES.SELECT:
                        return (
                            <FormField
                                key={attr.id}
                                label={
                                    <span>
                                        {attr.headerName}
                                        {!required && <i> - optional</i>}
                                    </span>
                                }
                                description={attr.description}
                                errorText={required ? state[`${attr.id}_error`] : ''}
                                info={attr?.infoContent}
                            >
                                <Select
                                    selectedOption={state[attr.id]}
                                    onChange={({ detail }) =>
                                        ComponentOnChangeTemplate(
                                            attr.checkDuplicate,
                                            required,
                                            COMPONENT_TYPES.SELECT,
                                            attr.id,
                                            detail.selectedOption,
                                            attr.headerName,
                                            inputChange,
                                            selectedItems,
                                            items,
                                            isInputInvalid,
                                            onIsInputInvalidChanged,
                                            onIsDataUnmodifiedChanged,
                                            formErrors,
                                            setFormErrors,
                                        )
                                    }
                                    options={attr.selections}
                                    disabled={getAttrDisabled(attr)}
                                ></Select>
                            </FormField>
                        )
                    case COMPONENT_TYPES.TOKEN_GROUP:
                        return modalMode === ModalModes.VIEW ? (
                            !(state[attr.id] || []).length ? (
                                <EmptyModalSection headerTitle={attr.headerName} />
                            ) : (
                                <SpaceBetween size={'s'} direction={'vertical'}>
                                    <Box variant={'h3'}>{attr.headerName}</Box>
                                    <TokenGroup
                                        i18nStrings={{
                                            limitShowFewer: `Show fewer ${attr.headerName}`,
                                            limitShowMore: `Show more  ${attr.headerName}`,
                                        }}
                                        items={
                                            state[attr.id].map((item) => ({
                                                ...item,
                                                disabled: true,
                                            })) || []
                                        }
                                        limit={3}
                                    />
                                </SpaceBetween>
                            )
                        ) : (
                            <FormField
                                key={attr.id}
                                label={
                                    <span>
                                        {attr.headerName}
                                        {!required && <i> - optional</i>}
                                    </span>
                                }
                                description={attr.description}
                                info={attr?.infoContent}
                            >
                                <SpaceBetween size={'s'}>
                                    <TokenGroup
                                        i18nStrings={{
                                            limitShowFewer: `Show fewer ${attr.headerName}`,
                                            limitShowMore: `Show more  ${attr.headerName}`,
                                        }}
                                        onDismiss={({ detail: { itemIndex } }) => {
                                            if ((state[attr.id] || []).length) {
                                                const temp = [
                                                    ...state[attr.id].slice(0, itemIndex),
                                                    ...state[attr.id].slice(itemIndex + 1),
                                                ]
                                                ComponentOnChangeTemplate(
                                                    attr.checkDuplicate,
                                                    required,
                                                    COMPONENT_TYPES.TOKEN_GROUP,
                                                    attr.id,
                                                    temp,
                                                    attr.headerName,
                                                    inputChange,
                                                    selectedItems,
                                                    items,
                                                    isInputInvalid,
                                                    onIsInputInvalidChanged,
                                                    onIsDataUnmodifiedChanged,
                                                    formErrors,
                                                    setFormErrors,
                                                )
                                                onIsDataUnmodifiedChanged(false)
                                            }
                                        }}
                                        items={state[attr.id] || []}
                                        limit={3}
                                    />
                                    <SpaceBetween size={'xs'} direction={'horizontal'}>
                                        <Input
                                            value={state[attr.tokenGroupInputId] || ''}
                                            onChange={({ detail }) =>
                                                inputChange(attr.tokenGroupInputId, detail.value)
                                            }
                                            disabled={getAttrDisabled(attr)}
                                        ></Input>
                                        <Button
                                            onClick={() => {
                                                const newAliases = (state[attr.id] || []).length
                                                    ? state[attr.id].map((item) => ({
                                                          label: item.label,
                                                          dismissLabel: `Remove ${item.label}`,
                                                      }))
                                                    : []
                                                const newAlias = state[attr.tokenGroupInputId] || ''
                                                newAliases.push({
                                                    label: newAlias,
                                                    dismissLabel: `Remove ${newAlias}`,
                                                })
                                                inputChange(attr.tokenGroupInputId, '')
                                                ComponentOnChangeTemplate(
                                                    attr.checkDuplicate,
                                                    required,
                                                    COMPONENT_TYPES.TOKEN_GROUP,
                                                    attr.id,
                                                    newAliases,
                                                    attr.headerName,
                                                    inputChange,
                                                    selectedItems,
                                                    items,
                                                    isInputInvalid,
                                                    onIsInputInvalidChanged,
                                                    onIsDataUnmodifiedChanged,
                                                    formErrors,
                                                    setFormErrors,
                                                )
                                            }}
                                            disabled={
                                                getAttrDisabled(attr) ||
                                                !state[attr.tokenGroupInputId] ||
                                                (attr?.tokenGroupLimit &&
                                                    (state[attr.id] || []).length ===
                                                        attr.tokenGroupLimit)
                                            }
                                        >
                                            {`Add ${attr.headerName}`}
                                        </Button>
                                    </SpaceBetween>
                                </SpaceBetween>
                            </FormField>
                        )
                    default:
                        return
                }
            })}
        </ColumnLayout>
    )
}

export default GroupFormFieldGenerator
